import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSpring, animated } from '@react-spring/web';
import { cn } from '@/lib/utils';
import { SPRING_SCROLL_CONFIG, useSpringScrollTo } from './useSpringScrollTo';

export type TocItem = { id: string; title: string };
export type TocGroup = { label?: string; items: TocItem[] };
export type TocSection = { id: string; label: string; groups: TocGroup[] };

const SPRING_CONFIG = SPRING_SCROLL_CONFIG;

/** A section's item list, with a spring-animated bar tracking the active item along the guide line. */
function TocSectionItems({
  section,
  activeId,
  onJump,
}: {
  section: TocSection;
  activeId: string;
  onJump: (id: string) => void;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [indicator, setIndicator] = useState({ top: 0, height: 0, visible: false });

  useLayoutEffect(() => {
    const track = trackRef.current;
    const activeEl = itemRefs.current[activeId];
    if (track && activeEl) {
      const trackRect = track.getBoundingClientRect();
      const activeRect = activeEl.getBoundingClientRect();
      setIndicator({ top: activeRect.top - trackRect.top, height: activeRect.height, visible: true });
    } else {
      setIndicator((prev) => ({ ...prev, visible: false }));
    }
  }, [activeId]);

  const spring = useSpring({
    top: indicator.top,
    height: indicator.height,
    opacity: indicator.visible ? 1 : 0,
    config: SPRING_CONFIG,
  });

  return (
    <div ref={trackRef} className="relative mt-1.5 space-y-2.5 pl-3">
      <div className="absolute bottom-0 left-0 top-0 w-px bg-border" />
      <animated.div
        className="absolute left-0 w-px bg-primary"
        style={{ top: spring.top, height: spring.height, opacity: spring.opacity }}
      />
      {section.groups.map((group, gi) => (
        <div key={group.label ?? gi} className="space-y-1.5">
          {group.label && (
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
              {group.label}
            </div>
          )}
          {group.items.map((item) => (
            <button
              key={item.id}
              ref={(el) => {
                itemRefs.current[item.id] = el;
              }}
              type="button"
              onClick={() => onJump(item.id)}
              className={cn(
                'block w-full truncate text-left text-[13px] transition-colors',
                activeId === item.id ? 'font-medium text-primary' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {item.title}
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Sticky "On this page" rail: lists every section/item in `sections`, spring-scrolls
 * `scrollContainerRef`'s content to the clicked anchor, and tracks scroll position to
 * highlight the current section/item automatically.
 *
 * Anchors are plain elements with `id={item.id}` inside `scrollContainerRef`. If the
 * caller filters `sections` (e.g. from a search box elsewhere on the page), this just
 * renders whatever it's given — an empty array renders the "No matches found" state.
 */
export function OnThisPageNav({
  sections,
  scrollContainerRef,
}: {
  sections: TocSection[];
  scrollContainerRef: React.RefObject<HTMLElement>;
}) {
  const [activeId, setActiveId] = useState<string>(sections[0]?.id ?? '');
  const scrollToId = useSpringScrollTo(scrollContainerRef);

  const allIds = useMemo(
    () => sections.flatMap((section) => [section.id, ...section.groups.flatMap((g) => g.items.map((i) => i.id))]),
    [sections]
  );

  useEffect(() => {
    const root = scrollContainerRef.current;
    if (!root) return;
    const targets = allIds.map((id) => document.getElementById(id)).filter((el): el is HTMLElement => el !== null);
    if (!targets.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;
        const topmost = visible.reduce((a, b) => (a.boundingClientRect.top < b.boundingClientRect.top ? a : b));
        setActiveId(topmost.target.id);
      },
      { root, rootMargin: '0px 0px -70% 0px', threshold: 0 }
    );
    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [allIds, scrollContainerRef]);

  const isSectionActive = (section: TocSection) =>
    activeId === section.id || section.groups.some((g) => g.items.some((i) => i.id === activeId));

  return (
    <aside className="custom-scrollbar hidden w-64 shrink-0 overflow-y-auto pl-4 lg:block">
      <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">On this page</div>
      {sections.length === 0 ? (
        <div className="text-xs text-muted-foreground">No matches found.</div>
      ) : (
        <nav className="space-y-4 pb-4 text-sm">
          {sections.map((section) => (
            <div key={section.id}>
              <button
                type="button"
                onClick={() => scrollToId(section.id)}
                className={cn(
                  'block w-full text-left text-[13px] font-semibold transition-colors',
                  isSectionActive(section) ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {section.label}
              </button>
              <TocSectionItems section={section} activeId={activeId} onJump={scrollToId} />
            </div>
          ))}
        </nav>
      )}
    </aside>
  );
}
